import { useMemo } from 'react';

import { useField, useForm } from 'react-final-form';

import { CAST_AI } from '@cast/constants';
import { ListItem, ListItemText, Stack } from '@cast/design-system';
import { useRegionsQuery } from '@cast/shared-inventory-data-access';
import { Cloud } from '@cast/shared-types';

import { useAbility } from '@cast/shared-ability-data-access';

import {
  CUSTOM_EDGE_PROVIDER,
  EDGE_PROVIDER_NAME_MAP,
  SUPPORTED_EDGE_PROVIDERS,
} from '../constants';
import { clusterToProvider } from '../utils';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { CspIcon } from '@cast/shared-csp-icons-ui';

import {
  RffInput,
  RffSelect,
  RffAutocomplete,
  useRffFormState,
} from '@cast/shared-rff-ui';

export const CloudSettingsSection = ({
  showCustomProviderOption,
}: {
  showCustomProviderOption?: boolean;
}) => {
  const { regions: _regions, isLoading: regionsLoading } = useRegionsQuery();

  const { batch, change } = useForm();
  const { mode } = useRffFormState();
  const isEditMode = mode === 'edit';
  const cloudProvider = useField('provider').input.value;

  const cloudProviderOptions = useMemo(() => {
    return SUPPORTED_EDGE_PROVIDERS.filter((provider) =>
      showCustomProviderOption ? true : provider !== CUSTOM_EDGE_PROVIDER
    ).map((provider) => ({
      label: EDGE_PROVIDER_NAME_MAP[provider],
      value: provider,
    }));
  }, [showCustomProviderOption]);

  const onlyClusterProviderEdgesFlag = useAbility().can(
    'view',
    'FeatureOnlyClusterProviderEdges'
  );
  const { cluster } = useCluster();
  const isEdgeProvider = !!clusterToProvider(cluster);
  const shouldShowOnlyClusterProviderEdges =
    onlyClusterProviderEdgesFlag && isEdgeProvider;

  const regions = useMemo(() => {
    return _regions?.filter((region) => region.csp === cloudProvider) || [];
  }, [_regions, cloudProvider]);

  return (
    <Stack gap={16}>
      <RffSelect
        testId="edge-location-provider-select"
        name="provider"
        options={cloudProviderOptions}
        optionLabel="label"
        optionValue="value"
        label="Provider"
        onChange={() => {
          batch(() => {
            change('region', undefined);
            change('projectId', undefined);
          });
        }}
        inputProps={{
          placeholder: 'Select cloud provider',
          hint: shouldShowOnlyClusterProviderEdges
            ? undefined
            : 'Can differ from your cluster provider.',
          readOnly: isEditMode || shouldShowOnlyClusterProviderEdges,
        }}
        renderOption={(option, reactProps) => {
          return (
            <ListItem
              {...reactProps}
              startAdornment={
                option.value === CUSTOM_EDGE_PROVIDER ? null : (
                  <CspIcon csp={option.value} size={16} />
                )
              }
            >
              <ListItemText>{option.label}</ListItemText>
            </ListItem>
          );
        }}
      />
      {cloudProvider === Cloud.gcp && (
        <RffInput
          testId="edge-location-project-id-input"
          name="projectId"
          label="GCP project ID"
          inputProps={{
            placeholder: 'e.g. gcp-project-123',
          }}
          readOnly={isEditMode}
        />
      )}
      {cloudProvider !== CUSTOM_EDGE_PROVIDER && (
        <RffAutocomplete
          testId="edge-location-region-select"
          name="region"
          options={regions}
          optionLabel="displayName"
          optionValue="name"
          label="Region"
          disabled={regionsLoading || cloudProvider === ''}
          inputProps={{
            placeholder: 'Select region',
            hint: `${CAST_AI} will use all availability zones.`,
            readOnly: isEditMode,
          }}
          onChange={(region) => {
            // Location name should match the selected region by default
            change('locationName', region.displayName);
          }}
        />
      )}
    </Stack>
  );
};
